/**
 * Column value resolvers for tables that sort and search by column config.
 * A column may supply getSortValue, getSearchValue, getDisplay, or getValue.
 * Otherwise the row field is read from column.key, including dotted paths.
 */

/**
 * Reads a dotted field path from a row.
 *
 * @param {Object} row - Data row.
 * @param {string} key - Dot-separated field path.
 * @returns {*} Field value, or undefined when the path is missing.
 */
function getByPath(row, key) {
  if (!key) return undefined;
  return key.split(".").reduce((obj, part) => obj?.[part], row);
}

/**
 * Resolves a row field for sorting or editing.
 *
 * @param {Object} row - Data row.
 * @param {Object} column - Column config.
 * @param {"getSortValue"|"getValue"} preferred - First resolver to try.
 * @returns {*} Resolved value.
 */
function resolveColumnValue(row, column, preferred) {
  if (typeof column[preferred] === "function") {
    return column[preferred](row);
  }
  if (preferred !== "getValue" && typeof column.getValue === "function") {
    return column.getValue(row);
  }
  return getByPath(row, column.key);
}

/**
 * Value used to sort a column.
 *
 * @param {Object} row - Data row.
 * @param {Object} column - Column config.
 * @returns {*} Sortable value.
 */
export function resolveSortValue(row, column) {
  return resolveColumnValue(row, column, "getSortValue");
}

/**
 * Value included in a table search box.
 * Prefers an explicit search value, then the displayed text, then the field value.
 *
 * @param {Object} row - Data row.
 * @param {Object} column - Column config.
 * @returns {*} Searchable value.
 */
export function resolveSearchValue(row, column) {
  if (typeof column.getSearchValue === "function") {
    return column.getSearchValue(row);
  }
  if (typeof column.getDisplay === "function") {
    return column.getDisplay(row);
  }
  return resolveColumnValue(row, column, "getValue");
}

/**
 * Value shown or edited in a cell.
 *
 * @param {Object} row - Data row.
 * @param {Object} column - Column config.
 * @returns {*} Cell value.
 */
export function resolveValue(row, column) {
  return resolveColumnValue(row, column, "getValue");
}

/**
 * Returns whether a row matches the live search query across any column.
 *
 * @param {Object} row - Data row.
 * @param {string} query - Search text.
 * @param {Array<Object>} columns - Column configs.
 * @returns {boolean} Whether the row should remain visible.
 */
export function rowMatchesSearch(row, query, columns) {
  const normalized = String(query || "").trim().toLowerCase();
  if (!normalized) return true;

  return columns.some((column) => {
    const value = resolveSearchValue(row, column);
    if (value === null || value === undefined) return false;
    return String(value).toLowerCase().includes(normalized);
  });
}
